/**
 * WHEN to hide the native launch splash (ipad-native-shell task 14.8): once
 * React has painted its first frame — never on a fixed timer, which either cuts
 * to a blank WebView on a slow iPad or holds a fast one on the logo for nothing.
 *
 * This module is only the timing. HOW to hide it is the platform adapter's job:
 * every `@capacitor/*` import lives in `src/platform/` (R7.6, enforced by
 * `platform/__tests__/adapterBoundary.test.ts`), so the caller passes the hide
 * function in. The shell lane wires it in `main.tsx`, inside `render()`:
 *
 *     hideSplashAfterFirstPaint(adapter.hideSplash);
 *
 * where the native adapter's `hideSplash` is
 * `Capacitor.isPluginAvailable("SplashScreen")` → `registerPlugin("SplashScreen").hide(...)`
 * — `registerPlugin` and not `Capacitor.Plugins.SplashScreen`, because since
 * Capacitor 3 the native side only publishes plugin headers and the proxy
 * exists only once something registers it. The web adapter passes `undefined`.
 * `capacitor.config.ts` sets `launchAutoHide: false` so this is what ends the
 * splash.
 */

export type HideSplash = (options: { fadeOutDuration: number }) => Promise<void> | void;

/** Long enough for the slowest supported iPad to paint; only reached if the
 *  app failed before its first frame. A splash that never leaves reads as a
 *  hung app, and the error UI (if any) is underneath it. */
export const SPLASH_SAFETY_NET_MS = 8000;

export const SPLASH_FADE_MS = 200;

let scheduled = false;

/**
 * Call once, right after `createRoot(...).render(...)`. Hides after two
 * animation frames — the first runs before React's commit is painted, the
 * second after it — with the safety net as the only timer. No-op when `hide`
 * is undefined (web, or no splash plugin). Idempotent.
 */
export function hideSplashAfterFirstPaint(hide: HideSplash | undefined): void {
  if (!hide || scheduled) return;
  scheduled = true;
  let done = false;
  const hideOnce = () => {
    if (done) return;
    done = true;
    try {
      void Promise.resolve(hide({ fadeOutDuration: SPLASH_FADE_MS })).catch(() => {
        /* already hidden, or the bridge is gone — nothing to recover */
      });
    } catch {
      /* same — a throwing hide must not take the app down with it */
    }
  };
  requestAnimationFrame(() => requestAnimationFrame(hideOnce));
  setTimeout(hideOnce, SPLASH_SAFETY_NET_MS);
}

/** TEST-ONLY. */
export function __resetSplashForTest(): void {
  scheduled = false;
}
